/**
 * Cancelling an intent. `cancelOrder` on chain invalidates it; the backend's
 * DELETE only stops dissemination and leaves the signature fillable. So the
 * chain goes first, and the backend's copy goes only once the invalidation is
 * mined. Demo sessions have no maker key: they get the removal alone, labelled so.
 */

import { api, ApiError } from "./api";
import { isReverted, waitForReceipt } from "./erc20";
import { sendCancelOrder } from "./lop";
import { getLopAddress } from "./runtimeConfig";
import type { Hex } from "./wallet";

/** Where a cancel has got to — drives the pending UI and the tx link. */
export type CancelStage =
  /** Waiting for the user to confirm the cancellation in their wallet. */
  | { kind: "wallet" }
  /** Sent; waiting for the receipt. Nothing has been removed from the book. */
  | { kind: "mining"; txHash: Hex }
  /** Invalidated on chain; removing the backend's copy. */
  | { kind: "book" };

export interface CancelOutcome {
  /** The invalidation transaction, or null for a removal alone. */
  txHash: Hex | null;
  /**
   * False when invalidated on chain but not dropped: the order is dead anyway,
   * the book merely stale.
   */
  bookRemoved: boolean;
}

/** Why a cancel could not even be attempted, or did not take effect. */
export type CancelFailure = "no-lop" | "reverted";

export class CancelError extends Error {
  readonly reason: CancelFailure;

  constructor(reason: CancelFailure, message: string) {
    super(message);
    this.name = "CancelError";
    this.reason = reason;
  }
}

export interface CancelParams {
  orderHash: string;
  /**
   * The order's own `makerTraits`, verbatim: `MakerTraitsLib.useBitInvalidator`
   * reads it to pick which invalidator `cancelOrder` bumps.
   */
  makerTraits: string;
  /** Connected wallet address, or null for a demo session. */
  maker: string | null;
  onStage?: (stage: CancelStage) => void;
}

/**
 * Resolves once the order can no longer be filled (wallet) or is dropped (demo).
 * Throws `WalletError` (on 4001 nothing is deleted: no "soft cancel"),
 * `CancelError` (`no-lop`, `reverted`) and, for demo sessions, `ApiError`.
 */
export async function cancelIntent(params: CancelParams): Promise<CancelOutcome> {
  const { orderHash, makerTraits, maker, onStage } = params;

  // --- demo session: the backend's record is all there is -------------------
  if (maker === null) {
    onStage?.({ kind: "book" });
    await api.deleteOrder(orderHash);
    return { txHash: null, bookRemoved: true };
  }

  const lopAddress = getLopAddress();
  if (lopAddress === null) {
    throw new CancelError(
      "no-lop",
      "The protocol address has not loaded yet — try again in a moment",
    );
  }

  // --- 1. on chain ---------------------------------------------------------
  onStage?.({ kind: "wallet" });
  const txHash = await sendCancelOrder({ maker, lopAddress, makerTraits, orderHash });

  onStage?.({ kind: "mining", txHash });
  const receipt = await waitForReceipt(txHash);
  if (isReverted(receipt)) {
    throw new CancelError(
      "reverted",
      "The cancellation reverted on chain — the order is still live",
    );
  }

  // --- 2. and only now, the book -------------------------------------------
  onStage?.({ kind: "book" });
  try {
    await api.deleteOrder(orderHash);
    return { txHash, bookRemoved: true };
  } catch (err) {
    // 404 and 409: the book already agrees with the chain. Anything else leaves
    // it stale, which is cosmetic: the order is invalidated on chain regardless.
    const settled = err instanceof ApiError && (err.status === 404 || err.status === 409);
    return { txHash, bookRemoved: settled };
  }
}
